import { ArrowRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline"
import checkCompletedIcon from "assets/svg/check-completed.svg"
import { Button, Spin } from "antd"
import { Button as CustomButton } from "components"
import { FAILED_CATEGORY_CONFIG, FAILED_GROUP_ORDER } from "../constants"
import type {
  FailedCategoryGroup,
  UploadTimeSheetFailedEntity,
} from "../types"
import { countAffected } from "../utils"
import StepActionBar from "../StepActionBar"

interface Props {
  isLoading: boolean
  isSubmitting: boolean
  failedUpload: UploadTimeSheetFailedEntity[]
  onBack: () => void
  onView: (failedType: string) => void
  onSubmit: () => void
}

const ErrorCard = ({
  failed,
  onView,
}: {
  failed: UploadTimeSheetFailedEntity
  onView: (failedType: string) => void
}) => {
  const config =
    FAILED_CATEGORY_CONFIG[failed.type] ?? FAILED_CATEGORY_CONFIG.unknown_error
  const Icon = config.Icon
  const count = countAffected(failed)
  return (
    <div className="flex flex-col gap-2 border-2 border-slate-200 rounded-md p-4 w-full font-circular">
      <div className="flex items-center gap-2">
        <Icon className={`w-6 h-6 shrink-0 ${config.iconColor}`} />
        <p className="font-bold text-base text-slate-900 m-0">{config.title}</p>
      </div>
      <p className="text-sm text-slate-500 m-0">{config.description(count)}</p>
      <button
        type="button"
        className="text-[#004AF5] text-sm font-semibold inline-flex items-center gap-1 self-start"
        onClick={() => onView(failed.type)}
      >
        {config.linkLabel}
        <ArrowRightIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

const SuccessCard = () => (
  <div className="flex flex-col gap-2 border-2 border-slate-200 rounded-lg p-4 w-full font-circular">
    <div className="flex items-center gap-1.5">
      <img
        src={checkCompletedIcon}
        width={20}
        height={20}
        alt="check"
        className="shrink-0"
      />
      <p className="font-bold text-lg text-slate-900 m-0">All records are ok</p>
    </div>
    <p className="text-sm text-slate-500 m-0">
      Click submit below to import the records into the system.
    </p>
  </div>
)

const Step3Review = ({
  isLoading,
  isSubmitting,
  failedUpload,
  onBack,
  onView,
  onSubmit,
}: Props) => {
  const submitDisabled = isLoading || isSubmitting

  // Bucket failed entries by visual section (group), preserving FAILED_GROUP_ORDER.
  const buckets: Record<FailedCategoryGroup, UploadTimeSheetFailedEntity[]> = {
    duplicate: [],
    entry: [],
    field: [],
    content: [],
  }
  for (const failed of failedUpload) {
    const config =
      FAILED_CATEGORY_CONFIG[failed.type] ??
      FAILED_CATEGORY_CONFIG.unknown_error
    buckets[config.group].push(failed)
  }

  const sections = FAILED_GROUP_ORDER.filter(
    (group) => buckets[group].length > 0
  )

  return (
    <>
      <Button
        type="default"
        className="self-start font-semibold px-4 py-2 h-auto rounded-lg border-2 border-[#E2E8F0] text-[#0F172B] flex items-center gap-2 mb-4"
        icon={<ChevronLeftIcon className="w-4 h-4" />}
        onClick={onBack}
      >
        Go back
      </Button>
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Review</h2>
      <p className="text-sm text-slate-500 pb-4">
        Any records with issues will be skipped, the records without issues will
        get imported.
      </p>
      {isLoading ? (
        <Spin />
      ) : sections.length === 0 ? (
        <SuccessCard />
      ) : (
        <div className="flex flex-col gap-3">
          {sections.map((group) => (
            <div
              key={group}
              className="flex flex-col gap-3 border-b-[6px] border-b-slate-100 pb-3 last:border-b-0 last:pb-0"
            >
              {buckets[group].map((failed) => (
                <ErrorCard
                  key={failed.type}
                  failed={failed}
                  onView={onView}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="pb-24" />

      <StepActionBar>
        <CustomButton
          color="primary"
          className="font-semibold"
          disabled={submitDisabled}
          // @ts-ignore
          submitting={isSubmitting}
          onClick={onSubmit}
        >
          Submit
        </CustomButton>
      </StepActionBar>
    </>
  )
}

export default Step3Review
